import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "redux/rootReducer";
import { Cell } from "react-table";
import { asEur, asPercent, seperateThousands } from "utility/numbers";
import { getReportDateSelector } from "redux/user/user";
import { loadAssetClassDistributionByAssetClass } from "redux/assetClassDistribution/assetClassDistributionFunctions";
import { Grid, useMediaQuery } from "@material-ui/core";
import Table from "components/Table";
import { AssetClassDistribution } from "types/AssetClassDistribution";
import { AssetClass } from "types/AssetClass";
import { getValueColor } from "utility/textColor";
import { dateToString } from "utility/date";
import { VisibleSections } from "types/VisibleSections";

interface Props {
  assetClass: AssetClass;
}

const AssetClassDistributionList = (props: Props) => {
  const { assetClass } = props;

  const { t } = useTranslation();

  const dispatch = useDispatch();

  const matchesMediaQuery = useMediaQuery("(min-width: 960px)");

  const reportControlId = useSelector(
    (state: RootState) => state.user.reportControlId
  );
  const reportDate = useSelector(getReportDateSelector);
  const assetClassDistribution = useSelector(
    (state: RootState) =>
      state.assetClassDistribution[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.data ?? []
  );
  const loading = useSelector(
    (state: RootState) =>
      state.assetClassDistribution[
        AssetClass[assetClass] as keyof typeof AssetClass
      ]?.loading
  );

  const assetClassDistributionGrouped = assetClassDistribution
    .filter((x) => !Boolean(x.AlaRivi))
    .reduce(
      (result, item) => ({
        ...result,
        [item.Kategoria]: [
          ...(result[item.Kategoria ?? ""] ?? []),
          {
            ...item,
            subRows:
              assetClassDistribution.filter(
                (x) => x.ParentRiviNumero === item.Rivinumero
              ) ?? [],
          },
        ],
      }),
      // tslint:disable-next-line: no-object-literal-type-assertion
      {} as { [key: string]: AssetClassDistribution[] }
    );

  const visibleSections = useSelector(
    (state: RootState) =>
      state.user.visibleSections?.data as VisibleSections | undefined
  );

  const parentRows = assetClassDistribution.filter(
    (x) => x.ParentRiviNumero == null
  );
  const assetClassDistributionColumns = Object.keys(
    assetClassDistributionGrouped
  ).map((a) => {
    const total = assetClassDistributionGrouped[a]?.find(
      (acd) => acd.Alakategoria === "Yhteensä"
    );

    const res = [
      {
        Header: a !== "SALKUT" ? a : "",
        accessor: "Alakategoria",
        paddingRight: "10px",
        width: 200,
        minWidth: 170,
        Footer: total?.Alakategoria ?? "",
      },
      {
        Header: t("weight"),
        accessor: "Paino",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          asPercent(cell.value, undefined, true),
        textAlign: "right",
        fontFamily: "Lato light",
        minWidth: 70,
        width: 70,
        Footer: asPercent(total?.Paino, undefined, true),
      },
      {
        Header: t("market_value"),
        accessor: "MarkkinaArvo",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          asEur(cell.value, undefined, true),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.MarkkinaArvo, undefined, true),
      },
      {
        Header: t("acquisition_value"),
        accessor: "HankintaArvo",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? asEur(cell.value, undefined, true)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asEur(total?.HankintaArvo, undefined, true),
      },
      {
        Header: t("count_pcs"),
        accessor: "Maara",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          seperateThousands(cell.value),
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: seperateThousands(total?.Maara),
      },
      {
        Header: t("dividend_and_other_profits"),
        accessor: "Tuottokassavirta",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asEur(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asEur(total?.Tuottokassavirta, undefined, true),
          total?.Tuottokassavirta
        ),
      },
      {
        Header: t("total_profit"),
        accessor: "TuottoEur",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asEur(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asEur(total?.TuottoEur, undefined, true),
          total?.TuottoEur
        ),
      },
      {
        Header: t("total_profit"),
        accessor: "Tuotto",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asPercent(total?.Tuotto, undefined, true),
          total?.Tuotto
        ),
      },
      {
        Header: t("profit_1m"),
        accessor: "Tuotto1kk",
        paddingRight: "10px",
        whiteSpace: "nowrap",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asPercent(total?.Tuotto1kk, undefined, true),
          total?.Tuotto1kk
        ),
      },
      {
        Header: t("profit_3m"),
        accessor: "Tuotto3kk",
        paddingRight: "10px",
        whiteSpace: "nowrap",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asPercent(total?.Tuotto3kk, undefined, true),
          total?.Tuotto3kk
        ),
      },
      {
        Header: t("profit_ytd"),
        accessor: "TuottoYTD",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? getValueColor(asPercent(cell.value, undefined, true), cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: getValueColor(
          asPercent(total?.TuottoYTD, undefined, true),
          total?.TuottoYTD
        ),
      },
      {
        Header: t("latest_price"),
        accessor: "Hinta",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? seperateThousands(cell.value, 2)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: " ",
      },
      {
        Header: t("date_of_price"),
        accessor: "HinnanPvm",
        paddingRight: "10px",
        Cell: (cell: Cell<AssetClassDistribution, Date>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? dateToString(cell.value)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: " ",
      },
      {
        Header: t("change"),
        accessor: "MuutosProsentti",
        Cell: (cell: Cell<AssetClassDistribution, number>) =>
          cell.row.original.Kategoria !== "Kassa"
            ? asPercent(cell.value, undefined, true)
            : "",
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asPercent(total?.MuutosProsentti, undefined, true),
        fontStyleOverride: undefined as
          | ((cell: Cell<AssetClassDistribution, number>) => string | undefined)
          | undefined,
      },
    ];

    if (Boolean(visibleSections?.NaytaESG)) {
      res.push({
        Header: t("impact_esg"),
        accessor: "NetImpactRatio",
        Cell: (cell: Cell<AssetClassDistribution, number>) => {
          const impactValue =
            cell.row.original.Kategoria !== "Kassa"
              ? asPercent(cell.value, 0, true)
              : "";

          if (
            Boolean(impactValue) &&
            impactValue !== "-" &&
            cell.row.original.IndustryAverageEsg
          ) {
            return <>* {impactValue}</>;
          }
          return impactValue;
        },
        textAlign: "right",
        fontFamily: "Lato light",
        Footer: asPercent(total?.NetImpactRatio, 0, true),
        fontStyleOverride: (cell: Cell<AssetClassDistribution, number>) =>
          Boolean(cell.row.original.IndustryAverageEsg) ? "italic" : undefined,
      });
    }

    return res;
  });

  React.useEffect(() => {
    if (reportControlId && reportDate) {
      dispatch(
        loadAssetClassDistributionByAssetClass(
          assetClass,
          reportControlId,
          reportDate
        )
      );
    }
  }, [dispatch, reportControlId, reportDate, assetClass]);

  return (
    <>
      {Object.keys(assetClassDistributionGrouped).map((a, i) => (
        <Grid
          key={a}
          item
          sm={12}
          style={{
            padding: matchesMediaQuery ? "0 0 16px 0" : "0 16px 16px 16px",
            overflowY: "hidden",
          }}
        >
          <Table
            data={assetClassDistributionGrouped[a].filter(
              (acb) => acb.Alakategoria !== "Yhteensä"
            )}
            columns={assetClassDistributionColumns[i]}
            loading={loading}
            hasFooter
            expandedRowIndexes={[
              assetClassDistributionGrouped[a].indexOf(
                assetClassDistributionGrouped[a].find((x) =>
                  x.Alakategoria.startsWith("Forea")
                ) as AssetClassDistribution
              ) ?? [],
            ]}
            subRows
          />
        </Grid>
      ))}
    </>
  );
};

export default React.memo(AssetClassDistributionList);
